import { FC, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePostHog } from '@posthog/react';
import { ArrowTopRightOnSquareIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import dayjs from '$/utils/dayjs/index.ts';
import { useTripContext } from '@/context/TripContext.tsx';
import bookingLinksFor, { type BookingItem } from '@/utils/booking/bookingLinksFor.ts';
import {
  type ControlTone,
  externalLinkClassName,
  externalLinkIconClassName,
  pillClassName,
} from '@/components/shared/tripControlClassNames.ts';
import { cn } from '@/utils/classNames.ts';

type BookLinksProps = {
  item: BookingItem;
  day: number;
  tone?: ControlTone;
};

const BookLinks: FC<BookLinksProps> = ({ item, day, tone = 'light' }) => {
  const { t } = useTranslation();
  const posthog = usePostHog();
  const { tripId, trip, itinerary } = useTripContext();
  const [isOpen, setIsOpen] = useState(false);
  const panelId = useId();
  const arrivalDate = trip?.data.arrival_date ?? itinerary?.data?.arrival_date ?? null;

  const links = useMemo(
    () => bookingLinksFor(item, { tripId, arrivalDate, data: trip?.data }, dayjs.utc().format('YYYY-MM-DD')),
    [item, tripId, arrivalDate, trip?.data]
  );

  if (links.length === 0) return null;

  const isHotel = item.kind === 'hotel';
  const placeName = isHotel ? item.stay.place : item.row.location.name;
  const event = {
    trip_id: tripId,
    item_type: item.kind,
    day,
    geoapify_place_id: (isHotel ? item.stay.geoapify_place_id : item.row.location.geoapify_place_id) ?? null,
  };

  const toggle = () => {
    if (!isOpen) {
      posthog?.capture('booking_list_opened', { ...event, partners: links.map((link) => link.partner) });
    }
    setIsOpen((open) => !open);
  };

  return (
    <>
      <button
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={toggle}
        className={cn(pillClassName, 'gap-1.5', tone === 'dark' && 'text-primary')}
      >
        {t(isHotel ? 'tripPage.book' : 'tripPage.tours_and_tickets')}
        <ChevronDownIcon
          aria-hidden="true"
          className={cn(
            'h-4 w-4 shrink-0 transition-transform duration-200 motion-reduce:transition-none',
            isOpen && 'rotate-180',
            tone === 'dark' ? 'text-primary' : 'text-gold-deep'
          )}
        />
      </button>
      {isOpen ? (
        <div id={panelId} className="order-last basis-full">
          <ul className="flex flex-col">
            {links.map((link) => (
              <li key={link.partner}>
                <a
                  href={link.url}
                  target="_blank"
                  rel="sponsored noopener"
                  aria-label={t(
                    isHotel ? 'tripPage.book_on_partner_label' : 'tripPage.tours_and_tickets_on_partner_label',
                    {
                      name: placeName,
                      partner: link.name,
                    }
                  )}
                  onClick={() => posthog?.capture('booking_link_clicked', { ...event, partner: link.partner })}
                  className={externalLinkClassName(tone)}
                >
                  {link.name}
                  <ArrowTopRightOnSquareIcon className={externalLinkIconClassName(tone)} aria-hidden="true" />
                </a>
              </li>
            ))}
          </ul>
          {isHotel && links.length > 1 ? (
            <p className={cn('mt-1 text-xs', tone === 'dark' ? 'text-white/70' : 'text-text-secondary')}>
              {t('tripPage.book_availability')}
            </p>
          ) : null}
          <p className={cn('mt-1 text-xs', tone === 'dark' ? 'text-white/70' : 'text-text-secondary')}>
            {t('tripPage.book_commission')}
          </p>
        </div>
      ) : null}
    </>
  );
};

export default BookLinks;
